// Mapa de órdenes de un batch con agrupación por zoom (clusters).
// Inspirado en Drivin / Onfleet: los pines individuales se ven al hacer
// zoom in; al alejar, se agrupan en círculos con la cuenta de órdenes.
// Usa Leaflet.markercluster directo — react-leaflet no provee wrapper
// estable, así que montamos el cluster group via useMap() y lo
// resincronizamos cuando cambian los puntos.

import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

// Lima como fallback cuando ningún punto tiene coords válidas.
const LIMA_CENTER: [number, number] = [-12.0464, -77.0428];

export type ClusterPoint = {
  id: string;
  lat: number;
  lon: number;
  /** Tooltip al hover/click sobre el marker individual. */
  label?: string;
};

type Props = {
  points: ClusterPoint[];
  /** Altura del contenedor; el width siempre es 100%. */
  height?: string;
};

export function BatchOrdersClusterMap({ points, height = "100%" }: Props) {
  // Centro inicial: promedio de los puntos válidos.
  const initialCenter = useMemo<[number, number]>(() => {
    if (points.length === 0) return LIMA_CENTER;
    let lat = 0, lon = 0, n = 0;
    for (const p of points) {
      if (Number.isFinite(p.lat) && Number.isFinite(p.lon)) {
        lat += p.lat;
        lon += p.lon;
        n++;
      }
    }
    return n === 0 ? LIMA_CENTER : [lat / n, lon / n];
  }, [points]);

  return (
    <MapContainer
      center={initialCenter}
      zoom={12}
      minZoom={4}
      maxZoom={22}
      scrollWheelZoom
      style={{ height, width: "100%" }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        maxZoom={22}
        maxNativeZoom={19}
      />
      <ClusterLayer points={points} />
      <FitOnFirstLoad points={points} />
    </MapContainer>
  );
}

// ----------------- Internals -----------------

// Sincroniza el cluster group con los puntos actuales. Recreamos el
// layer si cambia la cantidad — barato y robusto comparado con un diff.
function ClusterLayer({ points }: { points: ClusterPoint[] }) {
  const map = useMap();
  useEffect(() => {
    const cluster = (L as unknown as {
      markerClusterGroup: (opts?: Record<string, unknown>) => L.LayerGroup;
    }).markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      // Por defecto el cluster agrupa hasta zoom 19 — para una vista de
      // pedidos urbanos cortamos antes para que el operador pueda ver
      // pines individuales a nivel de cuadra.
      disableClusteringAtZoom: 17,
      maxClusterRadius: 60,
    });
    for (const p of points) {
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon)) continue;
      const marker = L.marker([p.lat, p.lon]);
      if (p.label) marker.bindTooltip(p.label);
      cluster.addLayer(marker);
    }
    map.addLayer(cluster);
    return () => {
      map.removeLayer(cluster);
    };
  }, [map, points]);
  return null;
}

// Al cargar la primera vez, encuadramos la vista a los puntos. Sin esto
// el zoom inicial 12 puede dejar todo fuera del viewport (datos en
// Arequipa cuando el fallback es Lima, p.ej.).
function FitOnFirstLoad({ points }: { points: ClusterPoint[] }) {
  const map = useMap();
  useEffect(() => {
    const valid = points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    if (valid.length === 0) return;
    const bounds = L.latLngBounds(valid.map((p) => [p.lat, p.lon] as [number, number]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    // Solo en el primer mount — el usuario puede re-encuadrar después.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}
